import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { getCookie } from 'cookies-next';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import { useAccount } from '@/frontastic';
import { ErrorNotification } from '@/helpers/utils/notification';
import { formatMobileNumber, getSiteDivisionCode, createUrl } from '@/helpers/utils/siteUtils';
import { AddDivisionPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { COOKIE_KEYS, SITE_KEY_MAP_MO } from '../../../../../constants/constant';
import { transformSuggestedAddress } from '../../create-customer/utils';
import { FORM_FIELD_SPECS } from '../../customer-detail/constants';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { toast } from '../../ui/use-toast';

const ConfirmCustomerModal = ({
  Texts,
  show,
  setShow,
  suggestedData,
  router,
  onSubmit,
  customerMasterData,
  customerId,
  allRowsData,
  setLoading,
  loading,
}: any) => {
  const { customerData, customerAddress } = transformSuggestedAddress(suggestedData);
  const { addDivision } = useAccount();
  const params = useSearchParams();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [getMailingRecordId, setGetMailingRecordId] = useState({ recordMailingIdProspect: '' });

  const handleAddDivision = async () => {
    if (
      suggestedData?.recordType === 'Prospect' &&
      suggestedData?.DivisionCustomers?.[0]?.CurrentPromotionInfo?.BestMailingRecord?.mailingRecordId
    ) {
      setGetMailingRecordId({
        recordMailingIdProspect:
          suggestedData?.DivisionCustomers?.[0]?.CurrentPromotionInfo?.BestMailingRecord?.mailingRecordId,
      });
      onSubmit();
    } else {
      const cookieDivisionCode = getCookie(COOKIE_KEYS.selectedSite);
      const divisionToAdd = getSiteDivisionCode(cookieDivisionCode ?? '');
      const payload: AddDivisionPayload = {
        query: {
          customerId: suggestedData.customerId,
          X_MAILING_RECORD_ID:
            suggestedData?.DivisionCustomers?.[0]?.CurrentPromotionInfo?.BestMailingRecord?.mailingRecordId ??
            customerMasterData?.DivisionCustomers?.[0]?.CurrentPromotionInfo?.BestMailingRecord?.mailingRecordId ??
            params?.get(FORM_FIELD_SPECS?.catalogId?.id) ??
            '',
        },
        data: {
          divisionCode: divisionToAdd,
        },
      };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const updatedAllRowsData = allRowsData?.map((item: any) => {
        if (item?.accountNumber === customerId) {
          item?.divisions?.divisionsData?.unshift(divisionToAdd);
        }
        return item;
      });

      setShow(false);
      setLoading(true);

      const divisionCode = getCookie(COOKIE_KEYS.selectedSite);
      const CDP_URL = createUrl(
        suggestedData?.customerId,
        suggestedData?.CustomerInfo?.omsCustomerId,
        getSiteDivisionCode(divisionCode ?? ''),
      );
      try {
        const alreadyHasDivision = suggestedData?.DivisionCustomers?.[0]?.divisionCode;
        const checkRecordType = suggestedData?.DivisionCustomers?.[0]?.recordType;
        if (!alreadyHasDivision || checkRecordType === 'Prospect') {
          const res = await addDivision(payload);
          if (res.code === 'OK') {
            router.push(`/customer-details/${CDP_URL}`);
          } else {
            setLoading(false);
            ErrorNotification(toast, {
              description: `${Texts?.addToDivisionError} ${divisionToAdd && SITE_KEY_MAP_MO?.[divisionToAdd]}`,
              action: <ErrorIcon className="h-5 w-5" />,
            });
          }
        } else {
          router.push(`/customer-details/${CDP_URL}`);
        }
      } catch (err) {
        setLoading(false);
        console.error(err);
        ErrorNotification(toast, {
          description: Texts?.genericError,
          action: <ErrorIcon className="h-5 w-5" />,
        });
      }
    }
  };

  return (
    <Dialog onOpenChange={(value) => setShow((prev: any) => ({ ...prev, state: value }))} open={show}>
      <DialogContent className="max-w-[900px] bg-white sm:rounded-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{Texts?.confirmNewCustomer}</DialogTitle>
          <DialogDescription className="text-black">{Texts?.pleaseConfirmCustomer}</DialogDescription>
        </DialogHeader>
        {loading && <Loader />}
        <div className="grid w-4/5 grid-cols-2 gap-4 text-sm font-normal">
          <div>
            {customerData?.map((items: any) => (
              <div className="flex gap-2" key={`key-${items?.field}`}>
                <div className="mb-3 flex-1">{items?.field}</div>
                <div className={`flex-1 font-semibold ${items?.mask ? 'gbmask' : ''}`}>
                  {!Array.isArray(items?.value) ? (
                    <div>{items?.value}</div>
                  ) : (
                    <div>
                      <p>
                        {items?.key === 'phoneNumber' &&
                          formatMobileNumber(items?.value?.[0]?.PhoneInfo?.phoneNumber ?? '')}
                        {items?.key === 'email' && items?.value?.[0]?.EmailInfo?.emailAddress}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div>
            {customerAddress?.map((items) => (
              <div className="flex gap-2" key={`key-${items?.field}`}>
                <p className="mb-3 flex-1 font-normal">{items?.field}</p>
                <div className={`flex-1 font-semibold ${items?.mask ? 'gbmask' : ''}`}>
                  {!Array.isArray(items?.value) ? (
                    <div>{items?.value}</div>
                  ) : (
                    <div className="text-blueLight underline">
                      {items?.value?.map((site) => (
                        <div key={site?.value}>{site?.name}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-4 pt-6">
          <Button className="text-semibold bg-deepBlue text-white" onClick={handleAddDivision}>
            {Texts?.yesItMatches}
          </Button>
          <Button className="text-semibold border-[1px] border-foggyGray text-black" onClick={() => onSubmit()}>
            {Texts?.noMatchFound}
          </Button>
          <Button className="underline" onClick={() => setShow((prev: any) => ({ ...prev, state: false }))}>
            {Texts?.cancel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmCustomerModal;
